// VOD API service for managing VOD assets (highlights and condensed matches)

import { api } from './api'
import type {
  VodAsset,
  VodPlayer,
  VodSeason,
  VodAssetsResponse,
  VodAssetFilters,
  VodTypesResponse,
  SeriesResponse,
  VodEpisode,
  VodRenditionPreview,
} from '../types/vod'

/**
 * Fetch list of VOD assets with optional filters
 */
export const fetchVodAssets = (filters?: VodAssetFilters): Promise<VodAssetsResponse> => {
  const apiParams: Record<string, string | number> = {}

  if (filters?.vod_type_code?.length)
    apiParams.vod_type_code = filters.vod_type_code.join(',')
  if (filters?.status) apiParams.status = filters.status
  if (filters?.publish_status) apiParams.publish_status = filters.publish_status
  if (filters?.start_date) apiParams.start_date = filters.start_date
  if (filters?.end_date) apiParams.end_date = filters.end_date
  if (filters?.search?.trim()) apiParams.search = filters.search.trim()
  if (filters?.match_id) apiParams.match_id = filters.match_id
  if (filters?.origin) apiParams.origin = filters.origin
  if (filters?.availability) apiParams.availability = filters.availability
  if (filters?.publish_state?.length)
    apiParams.publish_state = filters.publish_state.join(',')
  if (filters?.display_date_from) apiParams.display_date_from = filters.display_date_from
  if (filters?.display_date_to) apiParams.display_date_to = filters.display_date_to
  if (filters?.tags?.length) apiParams.tags = filters.tags.join(',')
  if (filters?.competitor_ids?.length)
    apiParams.competitor_ids = filters.competitor_ids.join(',')
  if (filters?.season_ids?.length)
    apiParams.season_ids = filters.season_ids.join(',')
  if (filters?.match_link) apiParams.match_link = filters.match_link
  if (filters?.playable) apiParams.playable = filters.playable
  if (filters?.visible_on_site) apiParams.visible_on_site = filters.visible_on_site
  if (filters?.sort_by) apiParams.sort_by = filters.sort_by
  if (filters?.sort_order) apiParams.sort_order = filters.sort_order
  if (filters?.page) apiParams.page = filters.page
  if (filters?.limit) apiParams.limit = filters.limit

  return api.get<VodAssetsResponse>('/vod/assets', apiParams)
}

/**
 * Fetch a single VOD asset by ID
 */
export const fetchVodAsset = (assetId: string): Promise<VodAsset> => {
  return api.get<VodAsset>(`/vod/assets/${assetId}`)
}

/**
 * Fetch active VOD types (for filter options and labels)
 */
export const fetchVodTypes = (): Promise<VodTypesResponse> => {
  return api.get<VodTypesResponse>('/vod/types')
}

/**
 * Update a VOD asset's editorial fields. duration_sec is probe-derived at
 * upload finalize and never editable; the server intersects this with the
 * per-asset policy, so vod_type_code and requires_subscription apply to
 * first-party assets only.
 */
export const updateVodAsset = (
  assetId: string,
  data: {
    tc_title?: string
    tc_description?: string
    tc_synopsis?: string
    display_date?: string
    vod_type_code?: string
    requires_subscription?: boolean | null
    clip_start_sec?: number | null
    clip_end_sec?: number | null
  }
): Promise<{ message: string; asset: VodAsset }> => {
  return api.put(`/vod/assets/${assetId}`, data)
}

/**
 * Requeue a failed packaging job (allowed only when the latest job is ERROR).
 * The pipeline's next scheduled pass resubmits; no immediate encode starts.
 */
export const retryVodPackaging = (
  assetId: string
): Promise<{ message: string; asset_id: string; master_version: number; work_class: string }> => {
  return api.post(`/vod/assets/${assetId}/packaging/retry`, {})
}

/**
 * Re-announce the VOD's current media revision to Amagi. Reuses the existing
 * idempotency key, so it is a new attempt at the same media rather than a new
 * version. 409s when there is nothing to resend or a send is already in flight.
 */
export const resendVodToAmagi = (
  assetId: string
): Promise<{ status: string; tc_pub_content_id: string }> => {
  return api.post(`/vod/assets/${assetId}/publishing/resend`, {})
}

/**
 * Publish a first-party VOD (HIDDEN → ACTIVE) so it can reach rails and
 * playback. 409s unless capabilities.can_publish is true.
 */
export const publishVodAsset = (
  assetId: string
): Promise<{ message: string; asset: VodAsset }> => {
  return api.post(`/vod/assets/${assetId}/publish`, {})
}

/**
 * Pull a published first-party VOD back out of sight (ACTIVE → HIDDEN).
 * 409s unless capabilities.can_unpublish is true.
 */
export const unpublishVodAsset = (
  assetId: string
): Promise<{ message: string; asset: VodAsset }> => {
  return api.post(`/vod/assets/${assetId}/unpublish`, {})
}

/**
 * Replace the players an editor has chosen. Players contributed by the linked
 * match are rebuilt from that match and are not part of this list.
 */
export const updateVodAssetPlayers = (
  assetId: string,
  playerIds: string[]
): Promise<{ message: string; players: VodPlayer[] }> => {
  return api.put(`/vod/assets/${assetId}/players`, { player_ids: playerIds })
}

/**
 * Replace the seasons on a VOD asset. The list sent is the list it ends up
 * with, the season a linked match contributed included.
 */
export const updateVodAssetSeasons = (
  assetId: string,
  seasonIds: string[]
): Promise<{ message: string; seasons: VodSeason[] }> => {
  return api.put(`/vod/assets/${assetId}/seasons`, { season_ids: seasonIds })
}

/**
 * Every series with its seasons, for the episode pickers. Read-only: series and
 * seasons are created by the backfill importer, not through the CMS.
 */
export const fetchSeries = (): Promise<SeriesResponse> => {
  return api.get<SeriesResponse>('/vod/series')
}

/**
 * Attach a VOD asset to a series season as an episode, or detach it by passing
 * null for both. The two travel together — the server rejects one without the
 * other, since a season with no number is unorderable and a number with no
 * season is the "episode 5 of what" the schema exists to prevent.
 */
export const updateVodAssetEpisode = (
  assetId: string,
  seasonId: string | null,
  episodeNumber: number | null
): Promise<{ status: string; episode: VodEpisode | null }> => {
  return api.put(`/vod/assets/${assetId}/episode`, {
    tc_series_season_id: seasonId,
    episode_number: episodeNumber,
  })
}

/**
 * Link a VOD asset to a match, or clear the link by passing null.
 */
export const updateVodAssetMatch = (
  assetId: string,
  tcMatchId: string | null
): Promise<{ message: string; tc_match_id: string | null }> => {
  return api.put(`/vod/assets/${assetId}/match`, { tc_match_id: tcMatchId })
}

/**
 * Link by the Sportradar event id instead of our own — "sr:sport_event:123"
 * or the bare digits. The server resolves it and 404s if it names nothing.
 */
export const linkVodAssetMatchByExtEventId = (
  assetId: string,
  extEventId: string
): Promise<{ message: string; tc_match_id: string | null }> => {
  return api.put(`/vod/assets/${assetId}/match`, { ext_event_id: extEventId })
}

/**
 * A playable URL for the asset's HLS or DASH rendition, minted with a grant the
 * VOD CDN accepts (VodRenditionPreview.expires_at says for how long).
 */
export const fetchVodRenditionPreview = (
  assetId: string,
  kind: 'HLS' | 'DASH'
): Promise<VodRenditionPreview> => {
  return api.get(`/vod/assets/${assetId}/renditions/${kind}/preview`)
}
